import { FC, useEffect, useState, useCallback } from 'react';
import { request } from 'src/common/request';
import { useCurrentSpace } from 'src/hooks/space';
import {
  IntegrationItem,
  IconData,
  BotDTO,
  IntegrationDetailResponse,
} from '@next-space/fe-api-idl';
import { Button } from '@flowus/common/components/button';
import { useOpenModal } from '@flowus/common/next-modal';
import { message } from '@flowus/common/components/message';
import { Avatar } from 'src/common/components/avatar';
import { IntegrationType } from 'src/views/dev/integrations/types';
import { LoadingIcon } from 'src/common/components/loading-icon';
import { Empty } from 'src/views/main/template/empty';
import dayjs from 'dayjs';
import { SelectSpacePage } from 'src/views/oauth/select-space-page';
import { useTocIds } from '../../aside/toc/use-toc-id';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { getCurrentUser } from 'src/hooks/user';
import { SelectPageSetting } from './select-page-setting';
import { ViewPath } from 'src/utils';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

export const Integrations: FC = () => {
  const { t } = useAppTranslation();
  const currentSpace = useCurrentSpace();
  const [integrations, setIntegrations] = useState<IntegrationItem[]>([]);
  const [loading, setLoading] = useState(true);

  const openModal = useOpenModal();

  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request.integration.getSpaceIntegrations(currentSpace.uuid);
      const items: IntegrationItem[] = res.integrations ?? [];
      setIntegrations(items ?? []);
    } catch (e) {
      message.error(t('components.integral_setting.integrations.fetch_apps_failed'));
    } finally {
      setLoading(false);
    }
  }, [currentSpace.uuid, t]);

  useEffect(() => {
    void fetchIntegrations();
  }, [fetchIntegrations]);

  const handleRevoke = (integration: IntegrationItem) => {
    openModal.warning({
      title: t('components.integral_setting.integrations.revoke_authorization_confirm'),
      content: (
        <div className="space-y-3">
          <p>
            <span>
              {t('components.integral_setting.integrations.revoke_confirm_message', {
                name: integration.name,
              })}
            </span>
          </p>
          <div className="text-t4 text-grey3 space-y-1">
            <p>{t('components.integral_setting.integrations.revoke_tip_1')}</p>
            <p>{t('components.integral_setting.integrations.revoke_tip_2')}</p>
            <p>{t('components.integral_setting.integrations.revoke_tip_3')}</p>
          </div>
          <p className="text-t4 text-grey3">
            {t('components.integral_setting.integrations.revoke_warning')}
          </p>
        </div>
      ),
      confirm: async () => {
        try {
          const botId = integration.bots?.[0]?.uuid;
          if (botId) {
            if (integration.type === 'internal') {
              //如果是内部应用就没办法删掉机器人了
              await request.integration.updateBotPages(botId, {
                blockIdsToAdd: [],
              });
            } else if (integration.type === 'external') {
              //如果是外部应用，则直接删掉机器人
              await request.integration.deleteBot(botId);
            }
            message.success(t('components.integral_setting.integrations.revoke_success'));
          }
          void fetchIntegrations(); // Refresh list
        } catch (e) {
          message.error(t('components.integral_setting.integrations.revoke_failed'));
        }
      },
      confirmText: t('common.confirm_cancel_btn'),
      cancelText: t('components.integral_setting.integrations.keep_authorization'),
      colorType: 'red',
    });
  };

  const handleSettings = async (e: React.MouseEvent, integration: IntegrationItem) => {
    const bot = integration.bots?.[0];
    if (!bot) {
      message.error(t('components.integral_setting.integrations.bot_not_found'));
      return;
    }
    openModal.modal({
      content: ({ onCloseModal }) => {
        return (
          <SelectPageSetting bot={bot} integration={integration} onCloseModal={onCloseModal} />
        );
      },
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return dayjs(date).format('YYYY-MM-DD');
  };

  if (loading) {
    return (
      <div className="h-40 flex items-center justify-center">
        <LoadingIcon />
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="flex justify-between items-center mb-6 px-6">
        <h2 className="text-grey3 text-t2">
          {t('components.integral_setting.integrations.authorized_apps')}
        </h2>
        <a className="underline" href={ViewPath.integrations} target="_blank">
          {t('common.developer_center')}
        </a>
      </div>

      {integrations.length > 0 ? (
        <div className="space-y-4 px-6">
          {integrations.map((integration) => {
            const isIconData = integration.icon && typeof integration.icon === 'object';
            const iconData = isIconData ? (integration.icon as IconData) : undefined;
            const iconUrl = typeof integration.icon === 'string' ? integration.icon : '';

            return (
              <div
                key={integration.id}
                className="border border-grey6 rounded-lg p-2 hover:border-grey4 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center flex-1">
                    {iconData ? (
                      <Avatar
                        name={integration.name || 'App'}
                        icon={iconData}
                        iconSize={40}
                        className="w-10 h-10 mr-4 rounded-md"
                      />
                    ) : iconUrl ? (
                      <img
                        src={iconUrl}
                        alt={`${integration.name || 'App'} Icon`}
                        className="w-10 h-10 rounded-md mr-4 object-contain bg-grey7"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-grey7 flex items-center justify-center mr-4">
                        <span className="text-grey3 text-t1 font-medium">
                          {(integration.name || 'A').substring(0, 1).toUpperCase()}
                        </span>
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <h3 className="text-t1 font-medium mr-3">
                          {integration.name || 'Unnamed App'}
                        </h3>
                        <span className="text-t4 text-grey3 bg-grey7 px-2 py-0.5 rounded">
                          {integration.type === IntegrationType.INTERNAL
                            ? t('common.app_plugin')
                            : t('common.external_app')}
                        </span>
                      </div>

                      <div className="text-t3 text-grey3 mb-2">
                        {t('components.integral_setting.integrations.authorized_on', {
                          date: formatDate(integration.createdAt),
                        })}
                      </div>

                      {integration.description && (
                        <p className="text-t3 text-grey2 mb-3 line-clamp-2">
                          {integration.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Button onClick={(e) => handleSettings(e, integration)}>
                      {t('common.page_access_permissions')}
                    </Button>
                    <Button onClick={() => handleRevoke(integration)}>
                      {t('components.integral_setting.integrations.revoke_authorization')}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="h-40 flex flex-col items-center justify-center">
          <p className="text-t3 text-grey3 mt-4 mb-6">
            {t('components.integral_setting.integrations.no_authorized_apps')}
          </p>
        </div>
      )}
    </div>
  );
};
